// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — sections d'une organisation : dispositifs (paginés, filtre d'état), à traiter,
// historique de structure (200 derniers événements), pièces (statuts et mandats).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, HandHeart, History, Inbox, Plus } from 'lucide-react';
import { Bouton, EtatVide, Pagination, Squelette } from '../../design/composants';
import {
  useDs, toArray, libelle, dateHeure, TYPES_DISPOSITIF, ETATS_DISPOSITIF, CYCLE_DISPOSITIF, ACTIONS_EVENEMENT, OBJETS_EVENEMENT,
  ETATS_ORGANISATION, STATUTS_MANDAT,
} from './api';
import { useLecture, ErreurServeur, AccesRefuseDs, Etat, PaginationDs, ListePieces } from './commun';
import { FenetreNouveauDispositif } from './FenetresStructure';

// ─── Dispositifs ─────────────────────────────────────────────────────────────────────────────
export function DispositifsSection({ organisation, peutPreparer }) {
  const ds = useDs();
  const naviguer = useNavigate();
  const [etat, setEtat] = useState('');
  const [page, setPage] = useState(1);
  const [creation, setCreation] = useState(false);
  const liste = useLecture(() => ds.dispositifs(organisation.id, { page, taille: 25, etat }), [ds, organisation.id, page, etat]);
  const elements = toArray(liste.donnees);
  const ouvrir = (d) => naviguer(`/dispositifs/${organisation.id}/dispositifs/${d.id}`);

  if (liste.erreur && liste.erreur.statut === 403) return <AccesRefuseDs erreur={liste.erreur} />;
  return (
    <>
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <div className="rh-vues rh-vues-ligne" role="group" aria-label="État des dispositifs" style={{ margin: 0 }}>
          {[['', 'Tous'], ...CYCLE_DISPOSITIF.map((c) => [c, libelle(ETATS_DISPOSITIF, c)])].map(([c, l]) => (
            <button key={c || 'tous'} type="button" className="rh-vue" aria-pressed={etat === c} onClick={() => { setEtat(c); setPage(1); }}>{l}</button>
          ))}
        </div>
        {peutPreparer && organisation.etat === 'ACTIVE' && <Bouton variante="primaire" Icone={Plus} onClick={() => setCreation(true)}>Nouveau dispositif</Bouton>}
      </div>
      <section className="rh-carte" style={{ padding: 0 }}>
        {liste.erreur && <div style={{ padding: 16 }}><ErreurServeur erreur={liste.erreur} /></div>}
        {!liste.donnees && !liste.erreur && <div style={{ padding: 16 }}><Squelette largeur="50%" /></div>}
        {liste.donnees && !elements.length && (
          <EtatVide Icone={HandHeart} titre={etat ? 'Aucun dispositif dans cet état' : 'Aucun dispositif'}
            texte={etat ? 'Choisissez un autre état.' : peutPreparer
              ? 'Créez un premier dispositif (prêt social, aide, campagne…) : il commence en brouillon, puis est simulé, soumis et validé par une autre personne.'
              : 'Aucun dispositif n\'a encore été préparé dans cette organisation.'} />
        )}
        {elements.length > 0 && (
          <>
            <div className="rh-conteneur-tableau">
              <table className="rh-tableau">
                <thead><tr><th>Dispositif</th><th>Type</th><th>État</th><th aria-label="Ouvrir" /></tr></thead>
                <tbody>
                  {elements.map((d) => (
                    <tr key={d.id} className="rh-ligne" tabIndex={0} onClick={() => ouvrir(d)} onKeyDown={(e) => { if (e.key === 'Enter') ouvrir(d); }}>
                      <td><span className="rh-cellule-principale">{d.libelle}</span><span className="rh-cellule-secondaire" style={{ display: 'block' }}>{d.code}{d.donnees_demo ? ' · démonstration' : ''}</span></td>
                      <td>{libelle(TYPES_DISPOSITIF, d.type)}</td>
                      <td><Etat table={ETATS_DISPOSITIF} code={d.etat} /></td>
                      <td style={{ width: 32, color: 'var(--sg-texte-4)' }}><ChevronRight size={16} aria-hidden="true" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationDs donnees={liste.donnees} onPage={setPage} />
          </>
        )}
      </section>
      {creation && (
        <FenetreNouveauDispositif organisationId={organisation.id} onFermer={() => setCreation(false)}
          onCree={(d) => { setCreation(false); ouvrir(d); }} />
      )}
    </>
  );
}

// ─── À traiter ───────────────────────────────────────────────────────────────────────────────
export function ATraiterSection({ organisation }) {
  const ds = useDs();
  const { donnees, erreur } = useLecture(() => ds.aTraiter(), [ds]);
  const elements = toArray(donnees).filter((a) => a.organisation_id === organisation.id);
  return (
    <section className="rh-carte">
      <ErreurServeur erreur={erreur} />
      {!donnees && !erreur && <Squelette largeur="50%" />}
      {donnees && !elements.length && <EtatVide Icone={Inbox} titre="Rien à traiter" texte="Aucun acte ne vous attend dans cette organisation. Les campagnes, prêts et aides s'ajouteront ici à leur mise en service." />}
      {elements.length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {elements.map((a) => (
            <li key={a.libelle} style={{ padding: '10px 0', borderBottom: '1px solid var(--sg-bord)', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span>{a.libelle}{a.nombre > 1 ? ` (${a.nombre})` : ''}</span>
              <Link className="rh-lien" to={a.lien}>Ouvrir</Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Historique de structure ────────────────────────────────────────────────────────────────
const TABLES_ETAT = { ORGANISATION: ETATS_ORGANISATION, MANDAT: STATUTS_MANDAT, DISPOSITIF: ETATS_DISPOSITIF };
const etatLisible = (objet, code) => (code ? libelle(TABLES_ETAT[objet] || {}, code) : '');
const PAR_PAGE = 25;

export function HistoriqueSection({ organisation }) {
  const ds = useDs();
  const [page, setPage] = useState(1);
  const { donnees, erreur } = useLecture(() => ds.evenementsOrganisation(organisation.id), [ds, organisation.id]);
  const lignes = toArray(donnees);
  if (erreur && erreur.statut === 403) return <AccesRefuseDs erreur={erreur} />;
  const pages = Math.max(1, Math.ceil(lignes.length / PAR_PAGE));
  const visibles = lignes.slice((page - 1) * PAR_PAGE, page * PAR_PAGE);
  return (
    <section className="rh-carte" style={{ padding: 0 }}>
      {erreur && <div style={{ padding: 16 }}><ErreurServeur erreur={erreur} /></div>}
      {!donnees && !erreur && <div style={{ padding: 16 }}><Squelette largeur="50%" /></div>}
      {donnees && !lignes.length && <EtatVide Icone={History} titre="Aucun événement" texte="Les créations, validations et changements d'état de l'organisation, de ses organes et de ses fonctions s'inscrivent ici." />}
      {lignes.length > 0 && (
        <>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Date</th><th>Objet</th><th>Événement</th><th>Changement d'état</th><th>Motif</th></tr></thead>
              <tbody>
                {visibles.map((e) => (
                  <tr key={`${e.objet_id}-${e.le}-${e.action}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{dateHeure(e.le)}</td>
                    <td>{libelle(OBJETS_EVENEMENT, e.objet_type)}</td>
                    <td>{libelle(ACTIONS_EVENEMENT, e.action)}</td>
                    <td>{e.de_etat ? `${etatLisible(e.objet_type, e.de_etat)} → ${etatLisible(e.objet_type, e.vers_etat)}` : etatLisible(e.objet_type, e.vers_etat)}</td>
                    <td>{e.motif || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} total={lignes.length} taille={PAR_PAGE} onPage={(p) => setPage(Math.min(Math.max(1, p), pages))} />
          {lignes.length >= 200 && <p className="rh-form-aide" style={{ padding: '0 16px 12px', margin: 0 }}>Les 200 derniers événements sont affichés.</p>}
        </>
      )}
    </section>
  );
}

// ─── Pièces (statuts, mandats) ───────────────────────────────────────────────────────────────
export function PiecesSection({ organisation }) {
  const ds = useDs();
  const mandats = useLecture(() => ds.mandats(organisation.id, { page: 1, taille: 200 }), [ds, organisation.id]);
  const pieces = [
    organisation.piece_statuts_id && { id: organisation.piece_statuts_id, contexte: 'Statuts de l\'organisation' },
    ...toArray(mandats.donnees).flatMap((m) => [
      m.piece_id && { id: m.piece_id, contexte: `Mandat de ${m.personne} — ${m.fonction_libelle}` },
      m.piece_fin_id && { id: m.piece_fin_id, contexte: `Fin du mandat de ${m.personne} — ${m.fonction_libelle}` },
    ]),
  ].filter(Boolean);
  return (
    <section className="rh-carte">
      <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>
        Pièces des statuts et des mandats de l'organisation. L'empreinte SHA-256 de chaque fichier est vérifiée par le serveur à chaque téléchargement ;
        une pièce ne se supprime jamais, elle peut seulement être remplacée avec un motif.
      </p>
      {mandats.erreur && mandats.erreur.statut !== 403 && <ErreurServeur erreur={mandats.erreur} />}
      {!mandats.donnees && !mandats.erreur ? <Squelette largeur="50%" />
        : <ListePieces pieces={pieces} vide="Aucune pièce n'est encore rattachée aux statuts ou aux mandats de cette organisation." />}
    </section>
  );
}
